import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoMark } from "./LogoMark";

const links = [
  { id: "chi-sono", label: "Chi Sono", testid: "header-nav-chi-sono" },
  { id: "approccio", label: "L'Approccio", testid: "header-nav-approccio" },
  { id: "servizi", label: "Servizi", testid: "header-nav-servizi" },
  { id: "studio", label: "Lo Studio", testid: "header-nav-studio" },
  { id: "contatti", label: "Contatti", testid: "header-nav-contatti" },
];

const scrollTo = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: -72, duration: 1.4 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled
          ? "bg-ink/85 backdrop-blur-xl border-gold/15"
          : "bg-ink/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto h-[72px] flex items-center justify-between">
        <button
          data-testid="header-nav-home"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
        >
          <LogoMark size={36} className="transition-transform duration-700 group-hover:rotate-90" />
          <span className="font-serif text-lg tracking-wide text-sand">
            Andrea <span className="italic text-gold">Moretti</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="header-desktop-nav">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-sand-muted hover:text-sand gold-underline pb-1 transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="header-cta-book"
            onClick={() => scrollTo("contatti")}
            className="ml-2 text-sm font-semibold text-ink bg-gold hover:bg-sand px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            Richiedi un colloquio
          </button>
        </nav>

        <button
          data-testid="header-mobile-menu-button"
          className="md:hidden text-sand p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="header-mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-gold/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => {
                    setOpen(false);
                    scrollTo(l.id);
                  }}
                  className="text-left text-sand-muted hover:text-gold text-base transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="header-cta-book-mobile"
                onClick={() => {
                  setOpen(false);
                  scrollTo("contatti");
                }}
                className="mt-2 text-center text-sm font-semibold text-ink bg-gold px-5 py-3 rounded-full"
              >
                Richiedi un colloquio
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
